import { inject, InjectionToken, type ValueProvider } from '@angular/core';

export interface HlmDateRangePickerConfig<T> {
  /**
   * If true, the date picker will close when the end date is selected.
   */
  autoCloseOnEndSelection: boolean;

  /**
   * Defines how the dates should be displayed in the UI.
   */
  formatDates: (dates: [T | undefined, T | undefined]) => string;

  /**
   * Defines how the dates should be transformed before saving to model/form.
   */
  transformDates: (dates: [T, T]) => [T, T];
}

function getDefaultConfig<T>(): HlmDateRangePickerConfig<T> {
  return {
    formatDates: (dates) =>
      dates
        .filter(Boolean)
        .map((date) => (date instanceof Date ? date.toDateString() : `${date}`))
        .join(' - '),
    transformDates: (dates) => dates,
    autoCloseOnEndSelection: true,
  };
}

const HlmDateRangePickerConfigToken = new InjectionToken<HlmDateRangePickerConfig<unknown>>(
  'HlmDateRangePickerConfig',
);

export function provideHlmDateRangePickerConfig<T>(
  config: Partial<HlmDateRangePickerConfig<T>>,
): ValueProvider {
  return { provide: HlmDateRangePickerConfigToken, useValue: { ...getDefaultConfig(), ...config } };
}

export function injectHlmDateRangePickerConfig<T>(): HlmDateRangePickerConfig<T> {
  const injectedConfig = inject(HlmDateRangePickerConfigToken, { optional: true });
  return injectedConfig ? (injectedConfig as HlmDateRangePickerConfig<T>) : getDefaultConfig();
}
